import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/**
 * Slim 2-px progress bar pinned to the top of the viewport while
 * TanStack Query has any query refetching or mutation in flight.
 * Mirrors the YouTube / GitHub global-loading affordance — small,
 * non-blocking, visible at a glance.
 *
 * Rendering rules:
 *   • Sub-200 ms work is hidden — flicker is worse than no bar at
 *     all for fast cache hits.
 *   • While work is in flight, the bar shows an indeterminate
 *     animation tinted brand-yellow.
 *   • After the last in-flight finishes, the bar fades out over
 *     250 ms instead of yanking off-screen.
 */
const SHOW_DELAY_MS = 200;
const FADE_OUT_MS = 250;

export function GlobalProgressBar(): JSX.Element | null {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const busy = fetching + mutating > 0;
  const [phase, setPhase] = useState<'hidden' | 'visible' | 'fading'>('hidden');

  // Delay the "show" so quick cache hits don't flicker, and delay
  // the "hide" so a fast follow-up fetch doesn't blink.
  useEffect(() => {
    if (busy) {
      if (phase === 'hidden') {
        const t = window.setTimeout(() => setPhase('visible'), SHOW_DELAY_MS);
        return () => window.clearTimeout(t);
      }
      // Stay visible while still busy.
      if (phase === 'fading') setPhase('visible');
      return undefined;
    }
    // Not busy.
    if (phase === 'visible') {
      setPhase('fading');
      const t = window.setTimeout(() => setPhase('hidden'), FADE_OUT_MS);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [busy, phase]);

  if (phase === 'hidden') return null;
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-0.5 overflow-hidden"
      style={{ opacity: phase === 'fading' ? 0 : 1, transition: 'opacity 250ms ease-out' }}
    >
      <div className="global-progress h-full" style={{ background: 'var(--brand-yellow)' }} />
    </div>
  );
}
